import { useEffect, useMemo, FC } from 'react'
import { useAtomValue } from 'jotai'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { SidebarHeader } from 'src/v2/components/sidebar-header'
import { HeaderCover } from 'src/v2/components/sidebar-header-cover'
import { Colors, Shapes } from 'src/components/theme'
import { SidebarHeaderContent } from 'src/v2/components/sidebar-header-content'
import LocationHeaderBG from 'src/assets/images/map-explorer/location-header.svg'
import { ShoppingCenterIcon } from 'src/components/insights/icons/shopping-center'
import { prettyPlaceType } from 'src/utils/string-modification-utils'
import { LocationDetails } from 'src/views/map-explorer/sidebar/panels/selected-location'
import { PersonalInsights } from 'src/components/sidebar-panels/personal-insights'
import { AvailableInsights } from 'src/components/sidebar-panels/available-insights'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { fullAddress } from 'src/utils/full-address'
import { UserListsPopover } from 'src/components/user-lists-popover'
import { AlphaMapMobilityBadge } from 'src/views/map-explorer/sidebar/panels/results/mobility-badge'
import { SelectedEntityTree } from 'src/views/map-explorer/sidebar/related-results/selected-entity-tree'
import { FlexColumn } from 'src/components/flex-containers'
import { MapExplorerPanelActions } from 'src/views/map-explorer/sidebar/panels/actions'

import * as coreAtoms from 'src/v2/state/core'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import * as userListsAtoms from 'src/atoms/user-lists'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { ErrorBoundary } from 'src/components/errors/error-boundary'

const ReportsWrapper = styled(FlexColumn)`
  padding: ${Shapes.spacing[5]}px 0;
`

const ReportsTitle = styled(Typography)`
  padding: 0 ${Shapes.spacing[4]}px;
`

type MapExplorerSelectedShoppingCenterProps = {
  showNavigationActions?: boolean
}

export const MapExplorerSelectedShoppingCenter: FC<MapExplorerSelectedShoppingCenterProps> = ({
  showNavigationActions = false,
}) => {
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const setUserListsPopoverAnchorElement = useSetRecoilState(userListsPopoverAtoms.anchorElement)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))
  const isSaved = useRecoilValue(userListsAtoms.isSaved(userListItem))
  const inEntitySelector = Boolean(useAtomValue(selectorAtoms.supportedSelections))
  const { clearSelected, getBackText, goBack } = useMapExplorerNavigation()

  const googleMapsUrl = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_SHOPPING_CENTER') {
      return
    }

    const encodedName = encodeURIComponent(selectedEntity.data.google.name || '')
    const [lng, lat] = selectedEntity.data.google.geometry.location
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${selectedEntity.data.google.place_id}&ll=${lat},${lng}`
    return mapsUrl
  }, [selectedEntity])

  const onClickSave = (evt: React.MouseEvent<HTMLElement>) => setUserListsPopoverAnchorElement(evt.currentTarget)

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_SHOPPING_CENTER') {
      return
    }
    setUserListItem({
      type: 'location',
      data: {
        locationId: selectedEntity.data['am-loc'].id,
        name: selectedEntity.data['am-loc'].name,
        address: fullAddress(selectedEntity.data['am-loc']),
      },
    })
  }, [selectedEntity])

  if (!selectedEntity || selectedEntity.type !== 'SELECTED_SHOPPING_CENTER') {
    return null
  }

  const location = selectedEntity.data['am-loc']
  const googleData = selectedEntity.data.google

  return (
    <>
      <ErrorBoundary wrap>
        <SidebarHeader
          cover={
            <>
              <HeaderCover
                icon={<ShoppingCenterIcon color={Colors.bgWhite} />}
                iconBackground={Colors.primaryBlue500}
                iconBorderColor={Colors.primaryBlue100}
                iconRadius="rounded"
                badge={prettyPlaceType(googleData?.types[0] || '')}
                image={LocationHeaderBG}
                onClickSave={onClickSave}
                saved={isSaved}
                savedCount={savedIn.length}
                backText={showNavigationActions ? getBackText() : undefined}
                onBackClick={showNavigationActions ? goBack : undefined}
                onClose={showNavigationActions ? clearSelected : undefined}
              />
              <UserListsPopover />
            </>
          }
          content={
            <SidebarHeaderContent
              title={location.name}
              titleAuxiliary={location.hasMobility && <AlphaMapMobilityBadge />}>
              <LocationDetails
                rating={googleData.rating}
                ratingCount={googleData.user_ratings_total}
                address={fullAddress(location)}
                phone={googleData.phone}
                website={googleData.website}
                googleMapsUrl={googleMapsUrl}
                hideGoogleStreetView={inEntitySelector}
              />
              <SelectedEntityTree />
            </SidebarHeaderContent>
          }
        />
      </ErrorBoundary>
      {!inEntitySelector && (
        <div>
          <ErrorBoundary wrap>
            <MapExplorerPanelActions
              save={{
                onClick: onClickSave,
                isSaved,
              }}
              copy={{
                label: 'Address',
                text: googleData.formatted_address,
              }}
              streetView
              googleMapsUrl={googleMapsUrl}
            />
          </ErrorBoundary>
          <ReportsWrapper gap={Shapes.spacing[4]}>
            <ReportsTitle variant="title2" color={Colors.neutral950}>
              Reports
            </ReportsTitle>
            <ErrorBoundary wrap>
              <PersonalInsights />
            </ErrorBoundary>
            <ErrorBoundary wrap>
              <AvailableInsights only="alphamap-location" />
            </ErrorBoundary>
          </ReportsWrapper>
        </div>
      )}
    </>
  )
}
